/**
 * E-commerce Model Configuration
 * COMPLETE extraction of ALL assumption fields from the existing E-commerce model
 * 
 * Source: /server/ecommerce/ecommerce-api.js (defaultAssumptions)
 * Source: /public/js/ecommerce-simple.js (gatherEcommerceAssumptions)
 * 
 * FIELD COUNT VERIFICATION:
 * - Traffic & Conversion Funnel: 4 fields
 * - Customer Purchase Behavior: 5 fields  
 * - Growth Rates: 5 fields
 * - Product & Operations: 5 fields
 * - Cost Structure: 7 fields
 * - Financial & Tax: 6 fields
 * - Working Capital: 5 fields
 * - User Details: 1 field (email)
 * - Backend Required: 7 fields (auto-filled)
 * TOTAL: ~45 fields
 */

import type { ModelConfig } from '@/types/models';
import { standardResultTabs } from '@/config/resultTabs';

export const ecommerceConfig: ModelConfig = {
  modelType: 'ecommerce',
  apiEndpoint: '/api/ecommerce',
  displayName: 'E-commerce',
  resultTabs: standardResultTabs,
  description: 'Online retail business model with traffic, conversion, and inventory metrics.',
  icon: '🛒',
  color: '#7c3aed', // Purple

  assumptionGroups: [
    // === GROUP 1: User Details ===
    {
      id: 'user-details',
      title: '👤 Your Details',
      fields: [
        {
          id: 'userEmail',
          label: 'Your Email (for updates)',
          type: 'email',
          placeholder: 'name@company.com',
          required: false,
        },
      ],
    },

    // === GROUP 2: Traffic & Conversion Funnel ===
    {
      id: 'traffic-conversion',
      title: '🌐 Traffic & Conversion',
      fields: [
        {
          id: 'monthlyWebsiteVisitors',
          label: 'Monthly Website Visitors',
          type: 'number',
          defaultValue: 15200,
          min: 0,
          step: 100,
          helpText: 'Total monthly unique visitors to your site',
        },
        {
          id: 'visitorToCustomerConversion',
          label: 'Visitor to Customer Conversion (%)',
          type: 'number',
          defaultValue: 3.2,
          min: 0,
          max: 100,
          step: 0.1,
          helpText: 'Percentage of visitors who become customers',
        },
        {
          id: 'organicTrafficPct',
          label: 'Organic Traffic (%)',
          type: 'number',
          defaultValue: 42,
          min: 0,
          max: 100,
          step: 1,
          helpText: 'Percentage of traffic from organic sources',
        },
        {
          id: 'paidAdSpendMonthly',
          label: 'Monthly Paid Ad Spend ($)',
          type: 'number',
          defaultValue: 28500,
          min: 0,
          step: 500,
          helpText: 'Monthly spending on paid advertising',
        },
      ],
    },

    // === GROUP 3: Customer Purchase Behavior ===
    {
      id: 'customer-behavior',
      title: '🛍️ Customer Purchase Behavior',
      fields: [
        {
          id: 'startingActiveCustomers',
          label: 'Starting Active Customers',
          type: 'number',
          defaultValue: 2850,
          min: 0,
          step: 10,
          helpText: 'Number of active customers at start',
        },
        {
          id: 'avgOrderValue',
          label: 'Average Order Value ($)',
          type: 'number',
          defaultValue: 89,
          min: 0,
          step: 1,
          helpText: 'Average dollar amount per order',
        },
        {
          id: 'ordersPerCustomerPerYear',
          label: 'Orders per Customer per Year',
          type: 'number',
          defaultValue: 4.2,
          min: 0,
          step: 0.1,
          helpText: 'Average number of orders per customer annually',
        },
        {
          id: 'customerRetentionRate',
          label: 'Customer Retention Rate (%)',
          type: 'number',
          defaultValue: 68,
          min: 0,
          max: 100,
          step: 1,
          helpText: 'Percentage of customers retained year-over-year',
        },
        {
          id: 'cac',
          label: 'Customer Acquisition Cost ($)',
          type: 'number',
          defaultValue: 68,
          min: 0,
          step: 1,
          helpText: 'Cost to acquire one new customer',
        },
      ],
    },

    // === GROUP 4: Growth Trajectory ===
    {
      id: 'growth-trajectory',
      title: '📈 Growth Trajectory',
      fields: [
        {
          id: 'growthRateY2',
          label: 'Growth Rate Year 2 (%)',
          type: 'number',
          defaultValue: 22,
          min: -100,
          max: 500,
          step: 0.1,
        },
        {
          id: 'growthRateY3',
          label: 'Growth Rate Year 3 (%)',
          type: 'number',
          defaultValue: 16,
          min: -100,
          max: 500,
          step: 0.1,
        },
        {
          id: 'growthRateY4',
          label: 'Growth Rate Year 4 (%)',
          type: 'number',
          defaultValue: 12,
          min: -100,
          max: 500,
          step: 0.1,
        },
        {
          id: 'growthRateY5',
          label: 'Growth Rate Year 5 (%)',
          type: 'number',
          defaultValue: 8,
          min: -100,
          max: 500,
          step: 0.1,
        },
      ],
    },

    // === GROUP 5: Product & Operations ===
    {
      id: 'product-operations',
      title: '📦 Product & Operations',
      fields: [
        {
          id: 'productCostPct',
          label: 'Product Cost (% of Price)',
          type: 'number',
          defaultValue: 52,
          min: 0,
          max: 100,
          step: 0.1,
          helpText: 'Cost of goods as percentage of selling price',
        },
        {
          id: 'shippingCostPerOrder',
          label: 'Shipping Cost per Order ($)',
          type: 'number',
          defaultValue: 7.8,
          min: 0,
          step: 0.1,
          helpText: 'Average shipping cost per order',
        },
        {
          id: 'returnRate',
          label: 'Return Rate (%)',
          type: 'number',
          defaultValue: 6.5,
          min: 0,
          max: 100,
          step: 0.1,
          helpText: 'Percentage of orders returned',
        },
        {
          id: 'inventoryTurnover',
          label: 'Inventory Turnover',
          type: 'number',
          defaultValue: 9,
          min: 1,
          max: 50,
          step: 0.5,
          helpText: 'Times inventory is sold per year',
        },
      ],
    },

    // === GROUP 6: Cost Structure ===
    {
      id: 'cost-structure',
      title: '💰 Cost Structure',
      fields: [
        {
          id: 'teamSize',
          label: 'Team Size',
          type: 'number',
          defaultValue: 18,
          min: 1,
          step: 1,
          helpText: 'Number of full-time employees',
        },
        {
          id: 'avgSalary',
          label: 'Average Salary ($)',
          type: 'number',
          defaultValue: 69000,
          min: 0,
          step: 1000,
          helpText: 'Average annual salary per employee',
        },
        {
          id: 'salesMarketingPct',
          label: 'Sales & Marketing (% of Revenue)',
          type: 'number',
          defaultValue: 24,
          min: 0,
          max: 100,
          step: 0.1,
        },
        {
          id: 'techCosts',
          label: 'Monthly Technology Costs ($)',
          type: 'number',
          defaultValue: 9000,
          min: 0,
          step: 100,
          helpText: 'Monthly platform, hosting, and software costs',
        },
        {
          id: 'paymentProcessingPct',
          label: 'Payment Processing (%)',
          type: 'number',
          defaultValue: 2.7,
          min: 0,
          max: 10,
          step: 0.01,
          helpText: 'Payment processing fees as % of revenue',
        },
        {
          id: 'fulfillmentCostPct',
          label: 'Fulfillment Cost (% of Revenue)',
          type: 'number',
          defaultValue: 5.5,
          min: 0,
          max: 30,
          step: 0.1,
          helpText: 'Warehouse and fulfillment costs',
        },
        {
          id: 'gaPct',
          label: 'G&A (% of Revenue)',
          type: 'number',
          defaultValue: 8,
          min: 0,
          max: 30,
          step: 0.1,
          helpText: 'General & administrative costs',
        },
      ],
    },

    // === GROUP 7: Financial & Tax ===
    {
      id: 'financial-tax',
      title: '🏛️ Financial & Tax',
      fields: [
        {
          id: 'corporateTaxRate',
          label: 'Corporate Tax Rate (%)',
          type: 'number',
          defaultValue: 25,
          min: 0,
          max: 60,
          step: 0.1,
        },
        {
          id: 'initialDebtAmount',
          label: 'Initial Debt Amount ($)',
          type: 'number',
          defaultValue: 300000,
          min: 0,
          step: 10000,
        },
        {
          id: 'annualInterestRate',
          label: 'Annual Interest Rate (%)',
          type: 'number',
          defaultValue: 8,
          min: 0,
          max: 30,
          step: 0.1,
        },
        {
          id: 'initialCapitalRaise',
          label: 'Initial Capital Raise ($)',
          type: 'number',
          defaultValue: 800000,
          min: 0,
          step: 50000,
        },
        {
          id: 'year3CapitalRaise',
          label: 'Year 3 Capital Raise ($)',
          type: 'number',
          defaultValue: 500000,
          min: 0,
          step: 50000,
        },
        {
          id: 'initialSetupCost',
          label: 'Initial Setup Cost ($)',
          type: 'number',
          defaultValue: 75000,
          min: 0,
          step: 5000,
        },
        {
          id: 'automaticCapitalRaise',
          label: 'Automatic Capital Raise',
          type: 'checkbox',
          defaultValue: true,
          helpText: 'Automatically raise capital when cash goes negative',
        },
      ],
    },

    // === GROUP 8: Working Capital ===
    {
      id: 'working-capital',
      title: '💵 Working Capital',
      fields: [
        {
          id: 'daysInAccountsReceivable',
          label: 'Days in Accounts Receivable',
          type: 'number',
          defaultValue: 5,
          min: 0,
          max: 365,
          step: 1,
          helpText: 'E-commerce is mostly cash/card',
        },
        {
          id: 'daysInAccountsPayable',
          label: 'Days in Accounts Payable',
          type: 'number',
          defaultValue: 30,
          min: 0,
          max: 365,
          step: 1,
        },
        {
          id: 'prepaidExpensesPct',
          label: 'Prepaid Expenses (% of OpEx)',
          type: 'number',
          defaultValue: 3,
          min: 0,
          max: 50,
          step: 0.1,
        },
        {
          id: 'accruedExpensesPct',
          label: 'Accrued Expenses (% of OpEx)',
          type: 'number',
          defaultValue: 5,
          min: 0,
          max: 50,
          step: 0.1,
        },
        {
          id: 'minimumCashBalance',
          label: 'Minimum Cash Balance ($)',
          type: 'number',
          defaultValue: 50000,
          min: 0,
          step: 5000,
        },
      ],
    },
  ],

};

// Export default assumptions matching server defaults exactly
export const ecommerceDefaultAssumptions = {
  // Traffic & Conversion Funnel
  monthlyWebsiteVisitors: 15200,
  visitorToCustomerConversion: 3.2,
  customerGrowthFromTraffic: 486,
  organicTrafficPct: 42,
  paidAdSpendMonthly: 28500,
  emailMarketingConversion: 8.5,

  // Customer Purchase Behavior
  avgOrderValue: 89,
  ordersPerCustomerPerYear: 4.2,
  startingActiveCustomers: 2850,
  customerRetentionRate: 68,
  newCustomerAcquisitionGrowth: 22,
  cac: 68,

  // Growth Trajectory
  growthRateY2: 22,
  growthRateY3: 16,
  growthRateY4: 12,
  growthRateY5: 8,

  // Product & Operations
  productCostPct: 52,
  shippingCostPerOrder: 7.8,
  returnRate: 6.5,
  inventoryTurnover: 9,
  avgInventoryDays: 40,

  // Cost Structure
  teamSize: 18,
  avgSalary: 69000,
  salesMarketingPct: 24,
  techCosts: 9000,
  paymentProcessingPct: 2.7,
  fulfillmentCostPct: 5.5,
  gaPct: 8,

  // Financial & Tax
  corporateTaxRate: 25,
  equipmentDepreciationRate: 20,
  softwareAmortizationRate: 33,
  initialEquipmentValue: 150000,
  initialSoftwareValue: 100000,
  initialDebtAmount: 300000,
  annualInterestRate: 8,
  debtRepaymentYears: 5,
  initialSetupCost: 75000,

  // Working Capital
  daysInAccountsReceivable: 5,
  daysInAccountsPayable: 30,
  prepaidExpensesPct: 3,
  accruedExpensesPct: 5,
  minimumCashBalance: 50000,

  // Capital Raising
  initialCapitalRaise: 800000,
  year2CapitalRaise: 0,
  year3CapitalRaise: 500000,
  year4CapitalRaise: 0,
  year5CapitalRaise: 0,
  automaticCapitalRaise: true,
};
